/**
 * Paper numbering — pure functions shared with the backend. Mirror of
 * `backend/src/utils/paperNumbering.ts` — keep both files 1:1. If you add a
 * new style token, add it to both.
 */

export type NumberingStyle =
  | 'upperAlpha'      // A, B, C
  | 'lowerAlpha'      // a, b, c
  | 'upperRoman'      // I, II, III
  | 'lowerRoman'      // i, ii, iii
  | 'numeric'         // 1, 2, 3
  | 'numericDot'      // 1., 2., 3.
  | 'numericParen'    // (1), (2), (3)
  | 'qPrefix'         // Q1, Q2, Q3
  | 'urduAbjad';      // الف، ب، ج، د

/**
 * How MCQ option labels are rendered — either counter-style (which uses the
 * option's index) or a bullet marker (identical for every option). Kept
 * distinct from NumberingStyle because bullets don't advance.
 */
export type MCQOptionStyle =
  | 'upperAlpha'  // A) B) C)
  | 'lowerAlpha'  // a) b) c)
  | 'upperRoman'  // I) II) III)
  | 'lowerRoman'  // i) ii) iii)
  | 'numeric'     // 1) 2) 3)
  | 'asterisk'    // *
  | 'bullet'      // •
  | 'dash';       // –

export interface PaperNumberingConfig {
  sectionStyle: NumberingStyle;
  subSectionStyle: NumberingStyle;
  questionStyle: NumberingStyle;
  subPartStyle: NumberingStyle;
  mcqOptionStyle: MCQOptionStyle;
}

export const DEFAULT_PAPER_NUMBERING: PaperNumberingConfig = {
  sectionStyle:    'upperAlpha',
  subSectionStyle: 'lowerAlpha',
  questionStyle:   'qPrefix',
  subPartStyle:    'lowerRoman',
  mcqOptionStyle:  'upperAlpha',
};

export type MarksDisplayMode = 'question' | 'section' | 'both';

export const MARKS_DISPLAY_MODES: MarksDisplayMode[] = ['question', 'section', 'both'];

export const MARKS_DISPLAY_MODE_LABELS: Record<MarksDisplayMode, string> = {
  question: 'Per question',
  section:  'Per section',
  both:     'Both',
};

export const NUMBERING_STYLES: NumberingStyle[] = [
  'upperAlpha', 'lowerAlpha', 'upperRoman', 'lowerRoman',
  'numeric', 'numericDot', 'numericParen', 'qPrefix',
  'urduAbjad',
];

export const NUMBERING_STYLE_LABELS: Record<NumberingStyle, string> = {
  upperAlpha:   'A, B, C',
  lowerAlpha:   'a, b, c',
  upperRoman:   'I, II, III',
  lowerRoman:   'i, ii, iii',
  numeric:      '1, 2, 3',
  numericDot:   '1., 2., 3.',
  numericParen: '(1), (2), (3)',
  qPrefix:      'Q1, Q2, Q3',
  urduAbjad:    'الف، ب، ج',
};

const URDU_ABJAD: string[] = [
  'الف', 'ب', 'ج', 'د', 'ہ', 'و', 'ز', 'ح', 'ط', 'ی',
  'ک', 'ل', 'م', 'ن', 'س', 'ع', 'ف', 'ص', 'ق', 'ر',
  'ش', 'ت', 'ث', 'خ', 'ذ', 'ض', 'ظ', 'غ',
];

const toUrduAbjad = (num: number): string => {
  if (num <= 0) return '';
  if (num <= URDU_ABJAD.length) return URDU_ABJAD[num - 1];
  return String(num);
};

const toRoman = (num: number): string => {
  if (num <= 0) return '';
  const map: Array<[number, string]> = [
    [1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'],
    [100, 'C'], [90, 'XC'], [50, 'L'], [40, 'XL'],
    [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I'],
  ];
  let n = num;
  let out = '';
  for (const [val, sym] of map) {
    while (n >= val) { out += sym; n -= val; }
  }
  return out;
};

const toAlpha = (num: number): string => {
  if (num <= 0) return '';
  let n = num;
  let out = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    out = String.fromCharCode(65 + rem) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
};

export const formatNumber = (ordinal: number, style: NumberingStyle): string => {
  if (!Number.isFinite(ordinal) || ordinal <= 0) return '';
  switch (style) {
    case 'upperAlpha':   return toAlpha(ordinal);
    case 'lowerAlpha':   return toAlpha(ordinal).toLowerCase();
    case 'upperRoman':   return toRoman(ordinal);
    case 'lowerRoman':   return toRoman(ordinal).toLowerCase();
    case 'numeric':      return String(ordinal);
    case 'numericDot':   return `${ordinal}.`;
    case 'numericParen': return `(${ordinal})`;
    case 'qPrefix':      return `Q${ordinal}`;
    case 'urduAbjad':    return toUrduAbjad(ordinal);
    default:             return String(ordinal);
  }
};

export const MCQ_OPTION_STYLES: MCQOptionStyle[] = [
  'upperAlpha', 'lowerAlpha', 'upperRoman', 'lowerRoman',
  'numeric', 'asterisk', 'bullet', 'dash',
];

export const MCQ_OPTION_STYLE_LABELS: Record<MCQOptionStyle, string> = {
  upperAlpha: 'A) B) C)',
  lowerAlpha: 'a) b) c)',
  upperRoman: 'I) II) III)',
  lowerRoman: 'i) ii) iii)',
  numeric:    '1) 2) 3)',
  asterisk:   '* (bullet)',
  bullet:     '• (bullet)',
  dash:       '– (dash)',
};

/**
 * Label for a single MCQ option. `ordinal` is 1-based (option A → 1). Counter
 * styles use the ordinal; bullet styles ignore it and return the same marker
 * for every option.
 */
export const formatOptionLabel = (ordinal: number, style: MCQOptionStyle): string => {
  switch (style) {
    case 'upperAlpha': return `${formatNumber(ordinal, 'upperAlpha')})`;
    case 'lowerAlpha': return `${formatNumber(ordinal, 'lowerAlpha')})`;
    case 'upperRoman': return `${formatNumber(ordinal, 'upperRoman')})`;
    case 'lowerRoman': return `${formatNumber(ordinal, 'lowerRoman')})`;
    case 'numeric':    return `${ordinal})`;
    case 'asterisk':   return '*';
    case 'bullet':     return '•';
    case 'dash':       return '–';
    default:           return `${formatNumber(ordinal, 'upperAlpha')})`;
  }
};
